import React, { useState, FC, useContext, useEffect, useCallback } from 'react';
import BitIconSpinner from '@narvar/nth-bit-icon-spinner';
import { ParamScheduleDeliveryCarrier } from '../../../server/api/schema';
import CustomComponentContext from '../CustomComponentContext';
import InitialPanel from './components/InitialPanel';
import CalendarPanel from './components/CalendarPanel';
import PanelContainer from './components/PanelContainer';
import PanelNav from './components/PanelNav';
import ScheduledPanel from './components/ScheduledPanel';
import { getAppointmentTimesDto, TPanel } from './types';
import ScheduledInfoPanel from './components/ScheduledInfoPanel';
import {
  getAppointmentTimes,
  getScheduledAppointment,
  rescheduleAppointment,
  scheduleAppointment,
} from './api';
import { StyledMainSpinnerContainer, StyledWrapper } from './styled';
import ExceptionPanel from './components/ExceptionPanel';
import { StatusMessageResponse } from '../../../server/lib/metro/types';
import { useTranslator } from './utils/translator';

const ScheduleDelivery: FC = () => {
  const { state, track, signalContentLoaded } = useContext(
    CustomComponentContext,
  );
  const {
    trackingNumbers,
    carrier,
    retailerMoniker,
    trackingStatusCode = '',
    localeLanguage,
  } = state.pageContext;

  const { t } = useTranslator();

  const [activePanel, setActivePanel] = useState<TPanel>('prompt');

  const [dismissed, setDismissed] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [reschedule, setReschedule] = useState(false);

  const [tempScheduledDate, setTempScheduledDate] = useState<Date>();
  const [scheduledDate, setScheduledDate] = useState<Date | null>();
  const [appointmentDateTimes, setAppointmentDateTimes] =
    useState<getAppointmentTimesDto>();
  const [statusMessage, _setStatusMessage] = useState<StatusMessageResponse>();
  const setStatusMessage = (newMessage: StatusMessageResponse) => {
    _setStatusMessage({ ...newMessage, message: t(newMessage.message) });
  };

  const [selectedTime, setSelectedTime] = useState<string>('');
  const [formattedSagawaDate, setFormattedSagawaDate] = useState<string>();
  const [timeSlotPresent, setTimeSlotPresent] = useState(false);

  useEffect(() => {
    if (!trackingNumbers) return;
    if (!carrier) return;

    const fetchAppointmentData = async () => {
      const json = await getScheduledAppointment(
        carrier,
        trackingNumbers[0],
        retailerMoniker,
      );

      return json;
    };

    const fetchSlotsData = async () => {
      const json = await getAppointmentTimes(
        carrier,
        trackingNumbers[0],
        retailerMoniker,
        trackingStatusCode,
      );

      return json;
    };

    (async () => {
      const [scheduledAppointment, appointmentSlots] = await Promise.all([
        fetchAppointmentData(),
        fetchSlotsData(),
      ]);

      setInitialLoading(false);
      setDismissed(false);

      // Appointment slots available
      if (Array.isArray(appointmentSlots)) {
        setAppointmentDateTimes(appointmentSlots);
        // Checking time slots are present or not
        const timesArrayPresent = appointmentSlots.some(
          (slot) => slot.times && slot.times.length > 0,
        );
        if (timesArrayPresent) {
          setTimeSlotPresent(true);
        }
      }

      // Has scheduled appointment
      if (scheduledAppointment.date !== null) {
        setScheduledDate(new Date(scheduledAppointment.date));
        setSelectedTime(
          scheduledAppointment.time ? scheduledAppointment.time : '',
        );
        setActivePanel('info');
        return;
      }

      // Cannot schedule appointment
      if (!scheduledAppointment.date && 'status' in appointmentSlots) {
        setStatusMessage(appointmentSlots);
        setActivePanel('exception');
        return;
      }

      // Show prompt to schedule appointment
      setActivePanel('prompt');
    })().catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    });
  }, [trackingNumbers, carrier, retailerMoniker]);

  useEffect(() => {
    if (scheduledDate === undefined) return;
    if (appointmentDateTimes === undefined) return;

    setInitialLoading(false);
  }, [scheduledDate, appointmentDateTimes]);

  useEffect(() => {
    if (!signalContentLoaded) return;
    signalContentLoaded();
  }, [signalContentLoaded]);

  const handleDismiss = () => {
    setDismissed(true);
  };

  // Below condition is to pass the formatted date to sagawa
  // As if we get toISOString from client means getting time zone issue while converting in server side.
  useEffect(() => {
    if (tempScheduledDate) {
      const year = tempScheduledDate.getFullYear();
      const month = `0${tempScheduledDate.getMonth() + 1}`.slice(-2);
      const day = `0${tempScheduledDate.getDate()}`.slice(-2);

      setFormattedSagawaDate(`${year}-${month}-${day}`);
    }
  }, [tempScheduledDate]);

  const handleSubmit = useCallback(async () => {
    if (!trackingNumbers) return;
    if (!carrier) return;

    if (activePanel === 'prompt') {
      setActivePanel('form');
      return;
    }

    if (activePanel === 'form') {
      if (!tempScheduledDate) {
        return; // possibly throw error
      }

      setFetching(true);

      let json: StatusMessageResponse;

      if (reschedule) {
        json = await rescheduleAppointment(
          carrier,
          trackingNumbers[0],
          retailerMoniker,
          tempScheduledDate.toISOString(),
          selectedTime,
        );
      } else {
        json = await scheduleAppointment(
          carrier,
          trackingNumbers[0],
          retailerMoniker,
          tempScheduledDate.toISOString(),
          selectedTime,
          formattedSagawaDate,
        );
      }

      if (json.status !== 'Success') {
        setActivePanel('exception');
        setStatusMessage(json);
        setFetching(false);

        return;
      }

      setActivePanel('success');
      setScheduledDate(tempScheduledDate);
      setFetching(false);

      track({
        eventType: 'click',
        clickSubType: reschedule
          ? 'scheduledDeliveryRescheduleSuccess'
          : 'scheduledDeliveryScheduleSuccess',
      });

      // For mobile it may be a better experience
      // to post message to parent to scroll back to
      // top at this point.
    }
  }, [
    activePanel,
    trackingNumbers,
    retailerMoniker,
    carrier,
    reschedule,
    tempScheduledDate,
    track,
  ]);

  const handleDaySelect = (date: Date | undefined) => {
    if (date) {
      const formattedDate = new Date(date);
      setTempScheduledDate(formattedDate);
    } else {
      setTempScheduledDate(undefined);
      setSelectedTime('');
    }
  };

  const handleTimeSelect = (userSelectedTime: string) => {
    setSelectedTime(userSelectedTime);
  };

  const handleRescheduleClick = () => {
    if (!trackingNumbers) return;
    if (!carrier) return;

    setInitialLoading(true);

    getAppointmentTimes(
      carrier,
      trackingNumbers[0],
      retailerMoniker,
      trackingStatusCode,
    )
      .then((json) => {
        setInitialLoading(false);

        if ('status' in json) {
          setActivePanel('exception');
          setStatusMessage(json);
          return;
        }

        setAppointmentDateTimes(json);
        setActivePanel('form');
        setReschedule(true);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
  };

  const handleCancel = () => {
    if (reschedule) {
      setActivePanel('info');
      return;
    }

    setActivePanel('prompt');
  };

  useEffect(() => {
    if (activePanel === 'prompt') {
      setTempScheduledDate(undefined);
    }
  }, [activePanel]);

  const getSubmitDisabled = (): boolean | undefined => {
    if (activePanel !== 'form') {
      return false;
    }
    let disabled = !tempScheduledDate;
    if (!disabled && appointmentDateTimes && timeSlotPresent) {
      disabled = !selectedTime;
    }
    return disabled;
  };
  if (dismissed) return null;

  return (
    <PanelContainer
      featureName={state.settings.featureName?.toString()}
      panel={activePanel}
      onDismiss={handleDismiss}
    >
      {initialLoading ? (
        <StyledMainSpinnerContainer>
          <BitIconSpinner />
        </StyledMainSpinnerContainer>
      ) : (
        <StyledWrapper>
          <>
            {activePanel === 'prompt' && <InitialPanel />}
            {activePanel === 'form' && (
              <CalendarPanel
                featureName={state.settings.featureName?.toString()}
                dateTimes={appointmentDateTimes}
                onDaySelect={handleDaySelect}
                onTimeSelect={handleTimeSelect}
                localeLanguage={localeLanguage}
              />
            )}
            {activePanel === 'success' && scheduledDate && (
              <ScheduledPanel
                featureName={state.settings.featureName?.toString()}
                scheduledDate={scheduledDate}
                scheduledTime={selectedTime}
                onRescheduleClick={handleRescheduleClick}
                localeLanguage={localeLanguage}
              />
            )}
            {activePanel === 'info' && scheduledDate && (
              <ScheduledInfoPanel
                carrier={carrier as ParamScheduleDeliveryCarrier}
                scheduledDate={scheduledDate}
                onRescheduleClick={handleRescheduleClick}
                scheduledTime={selectedTime}
                rescheduleEnabled={
                  scheduledDate !== null && appointmentDateTimes !== undefined
                }
                featureName={state.settings.featureName?.toString()}
                localeLanguage={localeLanguage}
              />
            )}
            {activePanel === 'exception' && statusMessage !== undefined && (
              <ExceptionPanel response={statusMessage} />
            )}
          </>
          <PanelNav
            panel={activePanel}
            loading={fetching}
            submitDisabled={getSubmitDisabled()}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        </StyledWrapper>
      )}
    </PanelContainer>
  );
};

export default ScheduleDelivery;
